import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Chip,
  Autocomplete,
  Alert,
  CircularProgress,
  Switch,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  PhotoCamera,
  Delete,
  CheckCircle,
  ArrowBack,
  ArrowForward,
  InfoOutlined,
} from "@mui/icons-material";

// Keep these helper names if your project uses them.
// uploadVideoMultipart removed because video upload is replaced by checkbox.
import { uploadVideoMultipart } from "./helpers/videoUpload"; // NOTE: unused now if you want remove the file. Kept to avoid breaking imports if used elsewhere.
import { useNavigate } from "react-router-dom";

const initialProperty = {
  // type selection
  listingFor: "selling", // selling | rental
  propertyType: "plot", // depends on listingFor

  // common fields
  name: "",
  city: "",
  plotArea: "",
  plotUnit: "sqft",
  builtUp: "",
  carpetArea: "",
  superBuiltUp: "",
  priceUnit: "Cr",
  direction: "",
  buildingYear: "",
  homeLoan: false,
  rccType: "",
  naProperty: false,
  rate: "",
  floor: "",
  resale: false,
  location: "",
  imageIds: [],
  videoId: null,
  nearBy: [],
  description: "",
  // rental specific
  rent: "",
  deposit: "",
  // extra
  negotiable: false,
  lift: false,
  rentUnit: "Thousands",
  parking: false,
  // bhk/rk
  bhkType: "", // BHK or RK
  bhkCount: 0,

  // NEW: request professional videographer instead of uploading video
  requestVideographer: false,
};
const priceUnits = ["Crores", "Lakhs", "Thousands"];

const highlightsOptions = [
  "Global School",
  "Swimming Pool",
  "Shopping Mall",
  "Hospital",
  "Park",
  "Metro Station",
];

const LS_KEY = "add_property_draft_v1";

const API_URL = window.location.href.includes("localhost")
  ? process.env.REACT_APP_API_URL_LOCAL
  : process.env.REACT_APP_API_URL_PROD;

export default function AddSitesMultiStep() {
  const [activeStep, setActiveStep] = useState(0);
  const steps = ["Property Type", "Details", "Images", "Submit"]; // keep the original labels
  const [property, setProperty] = useState(initialProperty);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  // Video file states removed in favor of checkbox
  const [continueDisabled, setContinueDisabled] = useState(false);

  const [uploadingImages, setUploadingImages] = useState(false);
  const [imagesUploaded, setImagesUploaded] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);

  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [savedPropertyName, setSavedPropertyName] = useState("");
  const [loadingAuth, setLoadingAuth] = useState(true);

  const [loggedIn, setLoggedIn] = useState(false);

  const navigate = useNavigate();



  useEffect(() => {
    // load draft
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setProperty((p) => ({ ...p, ...parsed.property }));
        if (parsed.imagePreviews && Array.isArray(parsed.imagePreviews)) setImagePreviews(parsed.imagePreviews);
      }
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(""), 3500);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  useEffect(() => {
    if (errorMsg) {
      const t = setTimeout(() => setErrorMsg(""), 3500);
      return () => clearTimeout(t);
    }
  }, [errorMsg]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      // redirect immediately; don't set any state after this
      navigate("/auth", { replace: true });
      return; // stop further execution
    }
    // if token exists, mark user as logged in
    setLoggedIn(true);
    setLoadingAuth(false);
  }, [navigate]);



  if (loadingAuth) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "80vh" }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Checking authentication...</Typography>
      </Box>
    );
  }

  if (!loggedIn) {
    return null; // user is being redirected
  }



  const update = (patch) => setProperty((p) => ({ ...p, ...patch }));

  const saveDraft = () => {
    try {
      const payload = { property, imagePreviews };
      localStorage.setItem(LS_KEY, JSON.stringify(payload));
      setSuccessMsg("Draft saved to browser");
    } catch (e) {
      setErrorMsg("Unable to save draft");
    }
  };

  const clearDraft = () => {
    localStorage.removeItem(LS_KEY);
  };

  // --- File helpers (same API contract your backend already uses) ---
  const uploadFile = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const res = await fetch(`${API_URL}api/upload`, { method: "POST", body: fd });
    if (!res.ok) throw new Error("Upload failed");
    const json = await res.json();
    if (json.success && json.uploaded?.length > 0) return json.uploaded[0];
    throw new Error("Upload failed");
  };

  const getPresignedUrl = async (file) => {
    const res = await fetch(
      `${API_URL}api/presign?filename=${encodeURIComponent(file.name)}`
    );
    return res.json();
  };
  const uploadToS3 = async (file, onProgress) => {
    const { url, key } = await getPresignedUrl(file);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);

      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && onProgress) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      };

      xhr.onload = () => {
        if (xhr.status === 200) {
          resolve(key); // ✅ return only S3 object key
        } else {
          console.error("S3 Upload Error:", xhr.status, xhr.responseText);
          reject(new Error(`Upload failed: ${xhr.status} - ${xhr.responseText}`));
        }
      };

      xhr.onerror = () => reject(new Error("Network error during upload"));
      xhr.send(file);
    });
  };

  const uploadImages = async () => {
    if (!imageFiles.length) throw new Error("No images to upload");
    setUploadingImages(true);
    try {
      const ids = [];
      for (let i = 0; i < imageFiles.length; i++) {
        const key = await uploadToS3(imageFiles[i]); // ✅ presigned upload
        ids.push(key);
      }
      update({ imageIds: ids });
      setImagesUploaded(true);
      setSuccessMsg(`${ids.length} image${ids.length > 1 ? "s" : ""} uploaded`);
      return ids;
    } finally {
      setUploadingImages(false);
    }
  };

  // --- validations per step ---
  // Requirement: remove most validations. Only images are required before submit.
  const validateStepImages = () => {
    if (!imageFiles.length && !imagePreviews.length && !property.imageIds?.length) {
      setErrorMsg("Please add at least one image");
      return false;
    }
    return true;
  };

  const handleNext = async () => {
    try {
      if (activeStep === 0) {
        // user selected listingFor and propertyType
        setActiveStep(1);
        return;
      }
      if (activeStep === 1) {
        setActiveStep(2);
        return;
      }
      if (activeStep === 2) {
        if (!validateStepImages()) return;
        // upload images if files exist and not uploaded yet
        if (imageFiles.length && !imagesUploaded) {
          try {
            await uploadImages();
          } catch {
            setErrorMsg("Image upload failed — try again");
            return;
          }
        }
        setActiveStep(3);
        return;
      }
      if (activeStep === 3) {
        // video upload removed. Instead, we proceed with the submit
        // property.requestVideographer indicates user requested professional videography
        // call handleSubmit without a videoId (server can note requestVideographer)
        await handleSubmit(); // optional videoId removed
      }
    } catch (err) {
      console.error(err);
      setErrorMsg("Something went wrong — try again");
    }
  };

 const actionButtonStyles = {
  textTransform: "none",
  fontWeight: 600,
  py: { xs: 1.3, sm: 1.5 }, // slightly reduced padding
  px: { xs: 1.5, sm: 2.5 }, // slightly reduced padding
  borderRadius: 2,
  minWidth: 120,
  fontSize: { xs: "0.75rem", sm: "0.85rem" }, // reduced font size
};

const continueButtonStyles = {
  ...actionButtonStyles,
  backgroundColor: "#1565C0",
  color: "#fff",
  "&:hover": { backgroundColor: "#1E88E5" },
  "&:disabled": { backgroundColor: "#90CAF9", color: "#E3F2FD" },
};

const outlinedButtonStyles = {
  ...actionButtonStyles,
  borderColor: "#1565C0",
  color: "#1565C0",
  "&:hover": { borderColor: "#42A5F5", color: "#42A5F5", backgroundColor: "#F1F8FF" },
};


  const handleSubmit = async (videoId = null) => {
    setSavingProperty(true);
    try {
      let userInfo = JSON.stringify(localStorage.getItem("user"));
      const payload = { ...property, videoId, created_by: "ADMIN", customer: userInfo};
      const res = await fetch(`${API_URL}api/add-property-customer`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Save failed");

      const json = await res.json();
      if (json.success) {
        setSavedPropertyName(property.name || `${property.propertyType}`);
        setActiveStep(4);
        clearDraft();
      } else {
        setErrorMsg(json.message || "Save failed");
      }
    } catch (e) {
      console.error(e);
      setErrorMsg("Error saving property");
    } finally {
      setSavingProperty(false);
    }
  };

  const handleBack = () => setActiveStep((s) => Math.max(0, s - 1));

  const onSelectImages = async (files) => {
    const selected = Array.from(files || []);
    if (!selected.length) return;
    const newPreviews = await Promise.all(
      selected.map(
        (f) =>
          new Promise((res) => {
            const reader = new FileReader();
            reader.onload = () => res(reader.result);
            reader.readAsDataURL(f);
          })
      )
    );
    setImageFiles((prev) => [...prev, ...selected]);
    setImagePreviews((prev) => [...prev, ...newPreviews]);
  };

  const removeImage = (index) => {
    setImageFiles((prev) => prev.filter((_, i) => i !== index));
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
    update({ imageIds: property.imageIds ? property.imageIds.filter((_, i) => i !== index) : [] });
  };

  // Helpers for showing fields based on selection
  const isSelling = property.listingFor === "selling";
  const isRental = property.listingFor === "rental";
  const pt = property.propertyType;

  // Render field sets per requirement (unchanged inputs preserved)
  const renderFieldsForType = () => {
    switch (pt) {
      case "plot":
        return (
          <>
            <TextField label="Property Name" value={property.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
            <TextField label="City" value={property.city} onChange={(e) => update({ city: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Plot Area" value={property.plotArea} onChange={(e) => update({ plotArea: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Direction" value={property.direction} onChange={(e) => update({ direction: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <FormControlLabel control={<Checkbox checked={property.homeLoan} onChange={(e) => update({ homeLoan: e.target.checked })} />} label="Home loan available" sx={{ mt: 1 }} />
            <FormControlLabel control={<Checkbox checked={property.naProperty} onChange={(e) => update({ naProperty: e.target.checked })} />} label="NA Property" sx={{ mt: 1 }} />

            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={isRental ? "Monthly Rent" : "Price"}
                value={isRental ? property.rent : property.rate}
                onChange={(e) =>
                  update(isRental ? { rent: e.target.value } : { rate: e.target.value })
                }
                fullWidth
              />

              <Select
                value={property.priceUnit}
                onChange={(e) => update({ priceUnit: e.target.value })}
                sx={{
                  minWidth: 120,
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#1565C0" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#42A5F5" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E88E5" },
                  color: "#1565C0",
                }}
              >
                {priceUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>

            </Box>

            {isRental && <TextField label="Deposit" value={property.deposit} onChange={(e) => update({ deposit: e.target.value })} fullWidth sx={{ mt: 2 }} />}
            <FormControlLabel control={<Checkbox checked={property.resale} onChange={(e) => update({ resale: e.target.checked })} />} label="Resale" sx={{ mt: 1 }} />
            <TextField label="Address" value={property.location} onChange={(e) => update({ location: e.target.value })} fullWidth multiline rows={2} sx={{ mt: 2 }} />
          </>
        );

      case "flat":
      case "shop":
        return (
          <>
            <TextField label={pt === "flat" ? "Apartment Name" : "Shop Name"} value={property.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
            {pt === "flat" && (
              <TextField label="Floor" value={property.floor || ""} onChange={(e) => update({ floor: e.target.value })} fullWidth sx={{ mt: 2 }} />
            )}
            <TextField label="Building Year" value={property.buildingYear} onChange={(e) => update({ buildingYear: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="City" value={property.city} onChange={(e) => update({ city: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Carpet Area" value={property.carpetArea} onChange={(e) => update({ carpetArea: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Super Built Up" value={property.superBuiltUp} onChange={(e) => update({ superBuiltUp: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Direction" value={property.direction} onChange={(e) => update({ direction: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <FormControlLabel control={<Checkbox checked={property.homeLoan} onChange={(e) => update({ homeLoan: e.target.checked })} />} label="Home loan available" sx={{ mt: 1 }} />
            <FormControlLabel control={<Checkbox checked={property.naProperty} onChange={(e) => update({ naProperty: e.target.checked })} />} label="NA Property" sx={{ mt: 1 }} />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={isRental ? "Monthly Rent" : "Price"}
                value={isRental ? property.rent : property.rate}
                onChange={(e) =>
                  update(isRental ? { rent: e.target.value } : { rate: e.target.value })
                }
                fullWidth
              />

              <Select
                value={isRental ? property.rentUnit : property.priceUnit}
                onChange={(e) =>
                  update(isRental ? { rentUnit: e.target.value } : { priceUnit: e.target.value })
                }
                sx={{ minWidth: 120 }}
              >
                {priceUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {isRental && <TextField label="Deposit" value={property.deposit} onChange={(e) => update({ deposit: e.target.value })} fullWidth sx={{ mt: 2 }} />}
            <FormControlLabel control={<Checkbox checked={property.resale} onChange={(e) => update({ resale: e.target.checked })} />} label="Resale" sx={{ mt: 1 }} />
            <FormControlLabel control={<Checkbox checked={property.lift} onChange={(e) => update({ lift: e.target.checked })} />} label="Lift" sx={{ mt: 1 }} />
            <FormControlLabel control={<Checkbox checked={property.parking} onChange={(e) => update({ parking: e.target.checked })} />} label="Parking" sx={{ mt: 1 }} />
            <TextField label="Address" value={property.location} onChange={(e) => update({ location: e.target.value })} fullWidth multiline rows={2} sx={{ mt: 2 }} />
          </>
        );

      case "bungalow":
        return (
          <>
            <TextField label="Bungalow Name" value={property.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
            <TextField label="City" value={property.city} onChange={(e) => update({ city: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Built Up" value={property.builtUp} onChange={(e) => update({ builtUp: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField
              label="Plot Area"
              value={property.plotArea}
              onChange={(e) => update({ plotArea: e.target.value })}
              fullWidth
              sx={{ mt: 2 }}
            />
            <TextField label="Direction" value={property.direction} onChange={(e) => update({ direction: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <FormControlLabel control={<Checkbox checked={property.homeLoan} onChange={(e) => update({ homeLoan: e.target.checked })} />} label="Home loan available" sx={{ mt: 1 }} />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={isRental ? "Monthly Rent" : "Price"}
                value={isRental ? property.rent : property.rate}
                onChange={(e) =>
                  update(isRental ? { rent: e.target.value } : { rate: e.target.value })
                }
                fullWidth
              />

              <Select
                value={isRental ? property.rentUnit : property.priceUnit}
                onChange={(e) =>
                  update(isRental ? { rentUnit: e.target.value } : { priceUnit: e.target.value })
                }
                sx={{ minWidth: 120 }}
              >
                {priceUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {isRental && <TextField label="Deposit" value={property.deposit} onChange={(e) => update({ deposit: e.target.value })} fullWidth sx={{ mt: 2 }} />}
            <FormControlLabel control={<Checkbox checked={property.resale} onChange={(e) => update({ resale: e.target.checked })} />} label="Resale" sx={{ mt: 1 }} />
            <TextField label="Address" value={property.location} onChange={(e) => update({ location: e.target.value })} fullWidth multiline rows={2} sx={{ mt: 2 }} />
          </>
        );

      case "rowhouse":
        return (
          <>
            <TextField label="Rowhouse Name" value={property.name} onChange={(e) => update({ name: e.target.value })} fullWidth />
            <TextField label="City" value={property.city} onChange={(e) => update({ city: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Built Up" value={property.builtUp} onChange={(e) => update({ builtUp: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <TextField label="Direction" value={property.direction} onChange={(e) => update({ direction: e.target.value })} fullWidth sx={{ mt: 2 }} />
            <FormControlLabel control={<Checkbox checked={property.homeLoan} onChange={(e) => update({ homeLoan: e.target.checked })} />} label="Home loan available" sx={{ mt: 1 }} />
            <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
              <TextField
                label={isRental ? "Monthly Rent" : "Price"}
                value={isRental ? property.rent : property.rate}
                onChange={(e) =>
                  update(isRental ? { rent: e.target.value } : { rate: e.target.value })
                }
                fullWidth
              />

              <Select
                value={isRental ? property.rentUnit : property.priceUnit}
                onChange={(e) =>
                  update(isRental ? { rentUnit: e.target.value } : { priceUnit: e.target.value })
                }
                sx={{ minWidth: 120 }}
              >
                {priceUnits.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {unit}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            {isRental && <TextField label="Deposit" value={property.deposit} onChange={(e) => update({ deposit: e.target.value })} fullWidth sx={{ mt: 2 }} />}
            <FormControlLabel control={<Checkbox checked={property.resale} onChange={(e) => update({ resale: e.target.checked })} />} label="Resale" sx={{ mt: 1 }} />
            <TextField label="Address" value={property.location} onChange={(e) => update({ location: e.target.value })} fullWidth multiline rows={2} sx={{ mt: 2 }} />
          </>
        );

      default:
        return null;
    }
  };

  const GuidanceCard = ({ title, children }) => (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        borderRadius: 2,
      }}
    >
      <Typography sx={{ fontWeight: 700, color: "#6B3E00", mb: 1 }}>{title}</Typography>
      <Box sx={{ mt: 1 }}>{children}</Box>
    </Paper>
  );



  return (
    <Box sx={{ minHeight: "80vh", py: 6, px: 2, background: "linear-gradient(180deg,#fffaf0 0%, #fff9f2 40%, #fff2e6 100%)", display: "flex", justifyContent: "center", alignItems: "flex-start" }}>
      <Box sx={{ width: "100%", maxWidth: 1100, bgcolor: "#fffefb", borderRadius: 3, p: { xs: 2, md: 4 }, boxShadow: 8, position: "relative", overflow: "hidden" }}>
        {/* Decorative golden circles */}
        <Box sx={{ position: "absolute", top: -100, right: -130, width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle at 30% 20%, rgba(245,200,110,0.55), transparent 30%), radial-gradient(circle at 80% 80%, rgba(242,183,69,0.35), transparent 30%)", filter: "blur(36px)", zIndex: 0 }} />
        <Box sx={{ position: "relative", zIndex: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#5A3B00", mb: 1, fontFamily: "Roboto, sans-serif" }}>
            Request Property Listing
          </Typography>

          <Typography sx={{ mb: 3, color: "#4D3B20", fontWeight: 400, fontFamily: "Roboto, sans-serif", fontSize: "14px" }}>
            List your property with ease — step-by-step guidance, thoughtful copy, and a professional touch</Typography>

          <Stepper
            activeStep={activeStep}
            alternativeLabel
           
          >
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      color: index === activeStep ? "#42A5F5" : "#1565C0",
                      fontWeight: 700,
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>


          {/* Step 0: Property Type */}
          {activeStep === 0 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <GuidanceCard title="Step 1 — Choose listing type">
                    <Typography variant="body2" sx={{ color: "#5a3b00" }}>
                      Selecting the correct listing type helps us show the most relevant fields and templates.
                    </Typography>
                  </GuidanceCard>

                  <Box sx={{ mt: 3 }}>
                    <Typography sx={{ mb: 2, fontWeight: 700, color: "#6b3e00" }}>Listing For</Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6}>
                        <Button variant={property.listingFor === "selling" ? "contained" : "outlined"} fullWidth onClick={() => update({ listingFor: "selling", propertyType: "plot" })}
                          sx={{
                            py: 1.8,
                            borderRadius: 2,
                            textTransform: "none",
                            fontWeight: 600,
                            background: property.listingFor === "selling"
                              ? "linear-gradient(90deg, #FFC857, #FF9F1C)"
                              : undefined,
                            color: property.listingFor === "selling" ? "#432A00" : "#6B3E00",
                            "&:hover": {
                              background: property.listingFor === "selling"
                                ? "linear-gradient(90deg, #FFB800, #E68A00)"
                                : "#FFF7E6",
                            },
                          }}>
                          Selling
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Button variant={property.listingFor === "rental" ? "contained" : "outlined"} fullWidth onClick={() => update({ listingFor: "rental", propertyType: "shop" })} sx={{ py: 1.8, borderRadius: 2, textTransform: "none", background: property.listingFor === "rental" ? "linear-gradient(90deg,#f6c477,#f0b23a)" : undefined }}>
                          Rental
                        </Button>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography sx={{ mt: 1, mb: 1, fontWeight: 600 }}>Choose Property Sub-type</Typography>
                        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                          {(property.listingFor === "selling" ? ["plot", "flat", "bungalow", "rowhouse", "shop"] : ["shop", "flat", "bungalow"]).map((ptype) => (
                            <Button key={ptype} variant={property.propertyType === ptype ? "contained" : "outlined"} onClick={() => update({ propertyType: ptype })} sx={{ textTransform: "none", borderRadius: 2 }}>
                              {ptype.charAt(0).toUpperCase() + ptype.slice(1)}
                            </Button>
                          ))}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <GuidanceCard title="Quick tips">
                    <ul style={{ marginTop: 0 }}>
                      <li>Choose accurately — this controls which attributes are shown.</li>
                      <li>You can save a draft and continue later.</li>
                    </ul>
                  </GuidanceCard>
                </Grid>

                <Grid item xs={12}>
                 <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", flexWrap: "wrap" }}>
  <Button
    startIcon={<ArrowBack />}
    onClick={handleBack}
    variant="outlined"
    sx={outlinedButtonStyles}
  >
    Back
  </Button>

  <Button
    onClick={saveDraft}
    variant="outlined"
    sx={outlinedButtonStyles}
  >
    Save Draft
  </Button>

  <Button
    endIcon={<ArrowForward />}
    onClick={handleNext}
    variant="contained"
    sx={continueButtonStyles}
  >
    Continue
  </Button>
</Box>

                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 1: Details (dynamic) */}
          {activeStep === 1 && (
            <Box component="form" noValidate>
              <Grid container spacing={3}>
                <Grid item xs={12} md={8}>
                  <GuidanceCard title="Step 2 — Property details">
                    <Typography variant="body2" sx={{ color: "#5a3b00" }}>
                      Enter accurate details — these will appear on the public listing. Add as much detail as possible for better leads.
                    </Typography>
                  </GuidanceCard>

                  <Box sx={{ mt: 3 }}>
                    {renderFieldsForType()}

                    {/* BHK / RK selection available for flats and some residential types */}
                    <Box sx={{ mt: 2, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, alignItems: "center" }}>
                      <Select value={property.bhkType} onChange={(e) => update({ bhkType: e.target.value })} sx={{ minWidth: 120 }}>
                        <MenuItem value="">Type</MenuItem>
                        <MenuItem value="BHK">BHK</MenuItem>
                        <MenuItem value="RK">RK</MenuItem>
                      </Select>

                      <Select value={property.bhkCount} onChange={(e) => update({ bhkCount: e.target.value })} sx={{ minWidth: 120 }}>
                        <MenuItem value={0}>Count</MenuItem>
                        <MenuItem value={1}>1</MenuItem>
                        <MenuItem value={2}>2</MenuItem>
                        <MenuItem value={3}>3</MenuItem>
                        <MenuItem value={4}>4</MenuItem>
                        <MenuItem value={5}>5</MenuItem>
                      </Select>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Typography>Negotiable</Typography>
                        <Switch checked={property.negotiable} onChange={(e) => update({ negotiable: e.target.checked })} />
                      </Box>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={4}>
                  <GuidanceCard title="Guidance & checks">
                    <Typography variant="body2" sx={{ color: "#5a3b00" }}>
                      Make sure address and price are correct; upload clear photos in the next step.
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Typography sx={{ fontWeight: 700 }}>Need help?</Typography>
                      <Typography variant="body2">Contact the admin or use Save Draft to continue later.</Typography>
                    </Box>
                  </GuidanceCard>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBack />}
                      onClick={handleBack}
                      sx={{
                        color: "#1565C0",
                        borderColor: "#1565C0",
                        textTransform: "none",
                        fontWeight: 600,
                        py: { xs: 1.5, sm: 1.8 },
                        px: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        "&:hover": { borderColor: "#42A5F5", color: "#42A5F5" },
                      }}
                    >
                      Back
                    </Button>                    <Button onClick={saveDraft} variant="outlined">Save Draft</Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={handleNext}
                      sx={{
                        backgroundColor: "#1565C0",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 600,
                        py: { xs: 1.5, sm: 1.8 },
                        px: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        "&:hover": { backgroundColor: "#1E88E5" },
                        "&:disabled": { backgroundColor: "#90CAF9", color: "#E3F2FD" },
                      }}
                    >
                      Continue
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 2: Images & Highlights */}
          {activeStep === 2 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <GuidanceCard title="Step 3 — Add images & highlights">
                    <Typography variant="body2" sx={{ color: "#5a3b00" }}>
                      High-quality, well-lit images attract far more leads. Add multiple angles and a clear exterior shot.
                    </Typography>
                  </GuidanceCard>

                  <Box sx={{ mt: 3 }}>
                    <Typography sx={{ mb: 1, fontWeight: 700 }}>Upload images</Typography>
                    <input accept="image/*" id="image-upload" type="file" multiple style={{ display: "none" }} onChange={(e) => onSelectImages(e.target.files)} />
                    <label htmlFor="image-upload">
                      <Button variant="contained" component="span" startIcon={<PhotoCamera />} sx={{ mb: 2, background: "linear-gradient(90deg,#f6c477,#f0b23a)", color: "#432a00" }}>Add Images</Button>
                    </label>

                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
                      {imagePreviews.length ? imagePreviews.map((src, i) => (
                        <Box key={i} sx={{ position: "relative", width: 140, height: 96, borderRadius: 2, overflow: "hidden", boxShadow: 3 }}>
                          <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          {uploadingImages ? <CircularProgress size={28} sx={{ position: "absolute", top: "35%", left: "35%" }} /> :
                            <CheckCircle color={imagesUploaded ? "success" : "disabled"} sx={{ position: "absolute", top: 6, left: 6 }} />}
                          <IconButton size="small" onClick={() => removeImage(i)} sx={{ position: "absolute", top: 6, right: 6, bgcolor: "rgba(255,255,255,0.85)" }}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Box>
                      )) : <Typography sx={{ color: "text.secondary" }}>No images selected</Typography>}
                    </Box>

                    <Autocomplete
                      multiple
                      freeSolo
                      options={highlightsOptions}
                      value={property.nearBy}
                      onChange={(e, val) => update({ nearBy: val })}
                      renderTags={(value, getTagProps) => value.map((option, index) => (<Chip key={index} label={option} {...getTagProps({ index })} />))}
                      renderInput={(params) => <TextField {...params} label="Nearby Highlights" placeholder="Add nearby highlights" sx={{ mt: 2 }} />}
                    />

                    <TextField
                      label="Property Description"
                      value={property.description}
                      onChange={(e) => { const words = e.target.value.split(/\s+/).slice(0, 300).join(" "); update({ description: words }); }}
                      fullWidth
                      multiline
                      rows={4}
                      sx={{ mt: 2 }}
                      helperText={`${property.description.split(/\s+/).filter(Boolean).length} / 300 words`}
                    />
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <GuidanceCard title="Preview & Tips">
                    <Typography sx={{ fontWeight: 700 }}>Photo checklist</Typography>
                    <ul style={{ marginTop: 0 }}>
                      <li>Clear, well-lit photos — avoid heavy shadows.</li>
                      <li>Show different angles and nearby landmarks.</li>
                      <li>Upload at least one exterior shot and one interior shot.</li>
                    </ul>
                    <Box sx={{ mt: 2 }}>
                      <Tooltip title="Images are required before final submit" placement="right">
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <InfoOutlined fontSize="small" />
                          <Typography variant="body2">Images are required for listing approval.</Typography>
                        </Box>
                      </Tooltip>
                    </Box>
                  </GuidanceCard>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBack />}
                      onClick={handleBack}
                      sx={{
                        color: "#1565C0",
                        borderColor: "#1565C0",
                        textTransform: "none",
                        fontWeight: 600,
                        py: { xs: 1.5, sm: 1.8 },
                        px: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        "&:hover": { borderColor: "#42A5F5", color: "#42A5F5" },
                      }}
                    >
                      Back
                    </Button>                    <Button onClick={saveDraft} variant="outlined">Save Draft</Button>
                    <Button
                      variant="contained"
                      endIcon={<ArrowForward />}
                      onClick={handleNext}
                      sx={{
                        backgroundColor: "#1565C0",
                        color: "#fff",
                        textTransform: "none",
                        fontWeight: 600,
                        py: { xs: 1.5, sm: 1.8 },
                        px: { xs: 2, sm: 3 },
                        borderRadius: 2,
                        "&:hover": { backgroundColor: "#1E88E5" },
                        "&:disabled": { backgroundColor: "#90CAF9", color: "#E3F2FD" },
                      }}
                    >
                      Continue
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Step 3: Video & Submit (now checkbox instead of video upload) */}
          {activeStep === 3 && (
            <Box>
              <Grid container spacing={3}>
                <Grid item xs={12} md={7}>
                  <GuidanceCard title="Step 4 — Submit">
                    <Typography variant="body2" sx={{ color: "#5a3b00" }}>
                      You can request our in-house professional videographers to produce a high-quality walkthrough video for your listing. This option helps buyers better understand the layout and increases engagement.
                    </Typography>
                  </GuidanceCard>

                  <Box sx={{ mt: 3 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={property.requestVideographer}
                          onChange={(e) => update({ requestVideographer: e.target.checked })}
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontWeight: 700 }}>Request our professional videographers</Typography>
                          <Typography variant="body2" sx={{ color: "text.secondary" }}>We will contact you to schedule a short on-site shoot. (No video upload required.)</Typography>
                        </Box>
                      }
                      sx={{ alignItems: "flex-start" }}
                    />

                    <Box sx={{ mt: 3 }}>
                      <Typography sx={{ mb: 1, fontWeight: 700 }}>Final notes</Typography>
                      <Typography variant="body2">After submission, the listing will be reviewed and published. You will be notified when it's live.</Typography>
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={5}>
                  <GuidanceCard title="Benefits of pro video">
                    <ul style={{ marginTop: 0 }}>
                      <li>Walkthroughs increase leads significantly.</li>
                      <li>Short 30–60s videos perform best.</li>
                      <li>We handle filming & editing — you approve before publish.</li>
                    </ul>
                  </GuidanceCard>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
                    <Button onClick={handleBack} startIcon={<ArrowBack />} variant="outlined">Back</Button>
                    <Button onClick={saveDraft} variant="outlined">Save Draft</Button>
                    <Button onClick={handleNext} variant="contained" endIcon={<ArrowForward />} sx={{ background: "linear-gradient(90deg,#f6c477,#f0b23a)", color: "#432a00" }} disabled={continueDisabled || savingProperty}>
                      {savingProperty ? <CircularProgress size={20} /> : "Submit Listing"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Success */}
          {activeStep === 4 && (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <CheckCircle sx={{ fontSize: 80, color: "#2e7d32", mb: 2 }} />
              <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>Success!</Typography>
              <Typography variant="h6" sx={{ mb: 1 }}>🎉 "{savedPropertyName}" has been added to the list!</Typography>
              <Typography variant="h6" sx={{ fontSize: 16, color: "text.secondary" }}>It's officially live on your customer's page (after review).</Typography>
              <Button onClick={() => {
                setActiveStep(0);
                setProperty(initialProperty);
                setImageFiles([]);
                setImagePreviews([]);
                setImagesUploaded(false);
              }} variant="contained" sx={{ mt: 4, background: 'linear-gradient(90deg,#cfa23a,#805000)', color: "#fff" }}>Add Another Property</Button>
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar open={!!successMsg} autoHideDuration={3000} onClose={() => setSuccessMsg("")} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="success" sx={{ width: '100%' }}>{successMsg}</Alert>
      </Snackbar>

      <Snackbar open={!!errorMsg} autoHideDuration={4000} onClose={() => setErrorMsg("")} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert severity="error" sx={{ width: '100%' }}>{errorMsg}</Alert>
      </Snackbar>

      <Dialog open={cancelConfirm} onClose={() => setCancelConfirm(false)}>
        <DialogTitle>Confirm Reset</DialogTitle>
        <DialogContent>Are you sure you want to clear the form? All inputs will be reset.</DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelConfirm(false)}>No</Button>
          <Button color="error" onClick={() => {
            setCancelConfirm(false);
            setProperty(initialProperty);
            setImageFiles([]);
            setImagePreviews([]);
            setImagesUploaded(false);
            clearDraft();
          }}>Yes, Reset</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
